import { message } from "antd";
import { Category } from "api/config/chalice-types";
import { ProgressUpdate } from "../hooks/useBulkAddTopics"; // Adjust if hook definition moves
import { InitialTopicData } from "../utils/csvParsing"; // Adjust if csvParsing is moved

// Type guard to check if an error is an object with a message property
const isErrorWithMessage = (error: unknown): error is { message: string } => {
  return typeof error === "object" && error !== null && "message" in error;
};

/**
 * Matches parsed topic data against existing categories and assigns IDs.
 */
export const matchExistingCategoryIds = (
  parsedData: InitialTopicData[],
  currentCategories: Category[]
): InitialTopicData[] => {
  const categoryMap = new Map<string, string>();
  currentCategories.forEach((cat) => {
    // Ensure category has a valid ID before adding to the map
    if (cat.id) {
      categoryMap.set(cat.name.toLowerCase(), cat.id);
    }
  });

  return parsedData.map((item) => {
    const existingId = categoryMap.get(item.categoryName.toLowerCase());
    // Only update categoryId if an existing ID is found
    return existingId ? { ...item, categoryId: existingId } : item;
  });
};

/**
 * Ensures categories exist for topics that don't have a categoryId yet,
 * creating them if necessary.
 * Returns a map of newly created category names (lowercase) to their IDs.
 */
export const ensureAndMapNewCategories = async (
  topicsInfo: InitialTopicData[],
  businessId: string | undefined,
  createCategoryFn: (params: {
    name: string;
    description: string;
  }) => Promise<Category>,
  onProgress: (update: ProgressUpdate) => void
): Promise<{
  newCategoryMap: Map<string, string>;
  errorsOccurred: boolean;
}> => {
  let creationErrorOccurred = false;
  const newCategoryMap = new Map<string, string>();

  // Filter topics that still need a categoryId (null)
  const topicsNeedingCreation = topicsInfo.filter(
    (item) => item.categoryId === null
  );
  // Get unique names of categories to be created
  const uniqueNewCategoryNames = Array.from(
    new Set(topicsNeedingCreation.map((d) => d.categoryName))
  );
  const totalToCreate = uniqueNewCategoryNames.length;

  if (!businessId) {
    message.error("Business context is missing. Cannot create categories.");
    // Return early if no businessId, indicating an error state
    return { newCategoryMap, errorsOccurred: true };
  }

  if (totalToCreate === 0) {
    // If no categories need creation, report progress and return
    onProgress({ current: 1, total: 0, message: "Creating topics" });
    return { newCategoryMap, errorsOccurred: false };
  }

  // Initial progress report for category creation
  onProgress({
    current: 0,
    total: totalToCreate,
    message: `0/${totalToCreate} categories created`,
  });

  let completed = 0;
  // Sequentially create each unique category
  for (const name of uniqueNewCategoryNames) {
    try {
      // Call the provided creation function
      const createdCategory = await createCategoryFn({ name, description: "" });
      if (createdCategory?.id) {
        // Map the lowercase name to the new ID
        newCategoryMap.set(name.toLowerCase(), createdCategory.id);
      } else {
        // Mark error if creation succeeded but no ID was returned
        creationErrorOccurred = true;
        console.error(`Category creation for "${name}" returned no ID.`);
        message.error(`Failed to get ID for created category: ${name}.`);
      }
    } catch (error) {
      // Mark error if creation failed
      creationErrorOccurred = true;
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : "Unknown error";
      console.error(`Failed to create category "${name}":`, error);
      message.error(
        `Failed to create category: ${name}. Error: ${errorMessage}`
      );
    }
    // Update progress after each attempt
    completed++;
    onProgress({
      current: completed,
      total: totalToCreate,
      message: `${completed}/${totalToCreate} categories created`,
    });
  }

  // Final progress update indicating transition to topic creation
  onProgress({
    current: totalToCreate + 1,
    total: totalToCreate,
    message: "Creating topics",
  });

  return { newCategoryMap, errorsOccurred: creationErrorOccurred };
};
